// GPH Conversation State Manager — dono do estado da conversa da Mi.
//
// Regra central: a Mi Brain conduz a conversa. Skills, quick actions, upload
// handlers e renderers executam decisões — nunca sobrescrevem o objetivo ativo.
//
// Este módulo é puro (sem UI, rede ou estado). Ele classifica cada entrada,
// encerra/rebaixa contexto antigo em troca de objetivo e decide a qual
// objetivo um vídeo anexado pertence.

// Classificação de cada nova entrada do usuário.
export const TURN_KINDS = Object.freeze({
  NEW_OBJECTIVE: 'new_objective',
  FOLLOW_UP_PREVIOUS_RESULT: 'follow_up_previous_result',
  CONTINUE_PENDING_FLOW: 'continue_pending_flow',
  ATTACHMENT_FOR_CURRENT_OBJECTIVE: 'attachment_for_current_objective',
  QUICK_ACTION_SELECTION: 'quick_action_selection',
  CONTEXT_RESET: 'context_reset'
});

const strip = (value = '') =>
  String(value).normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

// Troca explícita de objetivo: encerra/rebaixa o estado anterior.
// Cobre "vamos mudar", "começar outro problema", "agora quero outra coisa",
// "muda", "esquece isso", "quero analisar (um) video" após outro fluxo etc.
const CONTEXT_RESET_PATTERN = new RegExp([
  '\\bvamos mudar\\b',
  '\\bvamo mudar\\b',
  '\\bquero mudar\\b',
  '\\bpode mudar\\b',
  '\\bmudar de (assunto|objetivo|problema)\\b',
  '\\bcomecar outro problema\\b',
  '\\boutro problema\\b',
  '\\bagora quero outra coisa\\b',
  '\\bquero outra coisa\\b',
  '\\besquece (isso|essa|esse)\\b',
  '\\bdeixa isso (pra la|para la)\\b',
  '\\bnovo assunto\\b',
  '\\bassunto novo\\b'
].join('|'));

const BARE_RESET_PATTERN = /^(muda|mudar|troca|trocar)[!.\s]*$/;

const PREVIOUS_RESULT_PATTERN = /\b(esse|essa|isso|resultado|tabela|valores|anterior|ultima|ultimo)\b/;

export const detectContextReset = (text = '') => {
  const normalized = strip(text);
  if (!normalized) return false;
  return CONTEXT_RESET_PATTERN.test(normalized) || BARE_RESET_PATTERN.test(normalized);
};

export const classifyTurn = ({ text = '', hasAttachment = false, ctx = {} } = {}) => {
  if (hasAttachment) return TURN_KINDS.ATTACHMENT_FOR_CURRENT_OBJECTIVE;
  if (detectContextReset(text)) return TURN_KINDS.CONTEXT_RESET;
  if (ctx?.pendingSkill) return TURN_KINDS.CONTINUE_PENDING_FLOW;
  if (ctx?.lastSkillResult && PREVIOUS_RESULT_PATTERN.test(strip(text))) {
    return TURN_KINDS.FOLLOW_UP_PREVIOUS_RESULT;
  }
  return TURN_KINDS.NEW_OBJECTIVE;
};

// Encerra o objetivo anterior: limpa pendências e rebaixa resultados antigos
// para que não contaminem o novo objetivo. Preserva a memória permanente do
// jogador e o vídeo recente (o vídeo é um anexo físico, não um objetivo).
export const applyContextReset = (ctx = {}) => ({
  ...ctx,
  pendingSkill: null,
  pendingSlots: [],
  slots: {},
  pendingExecutionPlan: null,
  lastAssistantQuestion: null,
  lastSkillResult: null,
  lastGuidance: null,
  lastDiagnosis: null,
  lastTraining: null,
  lastAnalysis: null,
  lastCalculation: null,
  currentObjective: null,
  activeSkill: null,
  contextResetAt: new Date().toISOString()
});

// Um resultado anterior (diagnóstico/treino/análise) só vale como contexto
// vivo se aconteceu NESTA conversa e depois do último reset de objetivo.
// Isso impede que um "treino: cover" de dias atrás (reidratado do Firestore)
// capture um vídeo enviado com outro objetivo.
export const isFreshContextEntry = (entry, ctx = {}) => {
  if (!entry || !entry.at) return false;
  if (ctx?.conversationStartedAt && entry.at < ctx.conversationStartedAt) return false;
  if (ctx?.contextResetAt && entry.at < ctx.contextResetAt) return false;
  return true;
};

// ── Vídeo ────────────────────────────────────────────────────────────────────
const RECENT_VIDEO_MAX_AGE_MS = 30 * 60 * 1000;

export const registerVideoAttachment = (ctx = {}, file = null) => ({
  ...ctx,
  lastVideo: {
    name: file?.name || 'video',
    size: file?.size || null,
    at: new Date().toISOString()
  }
});

export const hasRecentVideo = (ctx = {}, host = {}) => {
  if (host?.selectedVideo) return true;
  const at = ctx?.lastVideo?.at;
  if (!at) return false;
  const age = Date.now() - new Date(at).getTime();
  return Number.isFinite(age) && age >= 0 && age <= RECENT_VIDEO_MAX_AGE_MS;
};

// analysisGoal pode chegar como capabilityId legado (mi_analysis_aim) ou como
// goal do contrato da skill (aim/movement/x1/gameplay/replay). Normaliza para
// o vocabulário do contrato.
const CAPABILITY_TO_GOAL = {
  mi_analysis_aim: 'aim',
  mi_analysis_movement: 'movement',
  mi_analysis_x1: 'x1',
  mi_analysis_gameplay: 'gameplay',
  mi_analysis_replay: 'replay'
};

export const normalizeAnalysisGoal = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return null;
  return CAPABILITY_TO_GOAL[raw] || raw;
};

const PENDING_VIDEO_SKILLS = new Set(['mi_analysis', 'video_analysis', 'video_goal']);

// Decide a qual objetivo um vídeo recém-anexado pertence.
// Prioridade:
//   1. objetivo de análise já ativo (pendência de vídeo) → usa o foco pedido,
//      ou análise geral quando o usuário pediu "analisar um vídeo" sem foco;
//   2. diagnóstico/treino DESTA conversa → validação vinculada;
//   3. sem objetivo ativo → perguntar o foco (nunca herdar contexto velho).
export const resolveVideoObjective = ({ ctx = {} } = {}) => {
  if (PENDING_VIDEO_SKILLS.has(ctx?.pendingSkill)) {
    return {
      kind: 'analysis',
      analysisGoal: normalizeAnalysisGoal(ctx?.slots?.analysisGoal) || 'gameplay',
      notes: null,
      linkedTo: null
    };
  }

  const diagnosis = ctx?.lastDiagnosis;
  if (isFreshContextEntry(diagnosis, ctx) && diagnosis.symptom) {
    return {
      kind: 'validation',
      analysisGoal: 'aim',
      notes: `Validar ajuste de sensibilidade (sintoma: ${diagnosis.symptom})`,
      linkedTo: { type: 'diagnosis', symptom: diagnosis.symptom, at: diagnosis.at }
    };
  }

  const training = ctx?.lastTraining;
  if (isFreshContextEntry(training, ctx) && (training.drillGoal || training.drillId)) {
    return {
      kind: 'validation',
      analysisGoal: 'gameplay',
      notes: `Validar execução do treino: ${training.drillGoal || training.drillId}`,
      linkedTo: { type: 'training', drillId: training.drillId, drillGoal: training.drillGoal, at: training.at }
    };
  }

  return { kind: 'ask_goal', analysisGoal: null, notes: null, linkedTo: null };
};

// ── Quick actions ────────────────────────────────────────────────────────────
// Sugestões são convite, não comando: no máximo MAX_NEXT_STEPS por resposta,
// sem duplicatas. O dock anterior deve ser expirado por quem renderiza.
export const MAX_NEXT_STEPS = 3;

export const limitNextSteps = (steps = [], max = MAX_NEXT_STEPS) => {
  const seen = new Set();
  const limited = [];
  for (const step of steps) {
    if (!step || seen.has(step)) continue;
    seen.add(step);
    limited.push(step);
    if (limited.length >= max) break;
  }
  return limited;
};

const conversationStateManager = {
  TURN_KINDS,
  classifyTurn,
  detectContextReset,
  applyContextReset,
  isFreshContextEntry,
  registerVideoAttachment,
  hasRecentVideo,
  normalizeAnalysisGoal,
  resolveVideoObjective,
  limitNextSteps,
  MAX_NEXT_STEPS
};

export default conversationStateManager;
